import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

function formatDate(d) {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date)) return d;
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
}

export default function Positions() {
  const [mstockPositions, setMstockPositions] = useState(null);
  const [zerodhaPositions, setZerodhaPositions] = useState(null);
  const [mstockOrders, setMstockOrders] = useState(null);
  const [zerodhaOrders, setZerodhaOrders] = useState(null);
  const [mstockAuth, setMstockAuth] = useState(false);
  const [zerodhaAuth, setZerodhaAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeView, setActiveView] = useState("positions"); // positions | orders

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");

    // Check auth for both
    try {
      const [mAuth, zAuth] = await Promise.all([
        axios.get("/api/mstock/auth-status").catch(() => ({ data: { authenticated: false } })),
        axios.get("/api/zerodha/auth-status").catch(() => ({ data: { authenticated: false } })),
      ]);
      setMstockAuth(mAuth.data.authenticated);
      setZerodhaAuth(zAuth.data.authenticated);

      const fetches = [];

      if (mAuth.data.authenticated) {
        fetches.push(
          axios.get("/api/mstock/positions").then(r => setMstockPositions(r.data.data)).catch(() => setMstockPositions(null)),
          axios.get("/api/mstock/orders").then(r => setMstockOrders(r.data.data)).catch(() => setMstockOrders(null)),
        );
      }
      if (zAuth.data.authenticated) {
        fetches.push(
          axios.get("/api/zerodha/positions").then(r => setZerodhaPositions(r.data.data)).catch(() => setZerodhaPositions(null)),
          axios.get("/api/zerodha/orders").then(r => setZerodhaOrders(r.data.data)).catch(() => setZerodhaOrders(null)),
        );
      }

      await Promise.all(fetches);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Parse mStock positions
  const mstockPos = [];
  if (mstockPositions?.net) {
    (Array.isArray(mstockPositions.net) ? mstockPositions.net : []).forEach(p => {
      mstockPos.push({
        broker: "mStock",
        symbol: p.tradingsymbol,
        exchange: p.exchange,
        qty: p.quantity,
        buyQty: p.buy_quantity,
        sellQty: p.sell_quantity,
        avgPrice: p.average_price,
        ltp: p.last_price,
        pnl: p.pnl,
        realised: p.realised,
        unrealised: p.unrealised,
        buyValue: p.buy_value,
        sellValue: p.sell_value,
        product: p.product,
      });
    });
  }

  // Parse Zerodha positions
  const zerodhaPos = [];
  if (zerodhaPositions?.net) {
    (Array.isArray(zerodhaPositions.net) ? zerodhaPositions.net : []).forEach(p => {
      zerodhaPos.push({
        broker: "Zerodha",
        symbol: p.tradingsymbol,
        exchange: p.exchange,
        qty: p.quantity,
        buyQty: p.buy_quantity,
        sellQty: p.sell_quantity,
        avgPrice: p.average_price,
        ltp: p.last_price,
        pnl: p.pnl,
        realised: p.realised,
        unrealised: p.unrealised,
        buyValue: p.buy_value,
        sellValue: p.sell_value,
        product: p.product,
      });
    });
  }

  const allPositions = [...mstockPos, ...zerodhaPos];
  const openPositions = allPositions.filter(p => p.qty !== 0);
  const closedPositions = allPositions.filter(p => p.qty === 0 && (p.buyQty > 0 || p.sellQty > 0));

  // Parse orders
  const parseOrders = (orders, broker) => {
    if (!orders) return [];
    const list = Array.isArray(orders) ? orders : orders?.orders || orders?.data || [];
    return list.map(o => ({
      broker,
      orderId: o.order_id || o.oms_order_id,
      symbol: o.tradingsymbol,
      exchange: o.exchange,
      type: o.transaction_type,
      qty: o.quantity,
      price: o.price || o.average_price,
      triggerPrice: o.trigger_price,
      status: o.status || o.order_status,
      orderType: o.order_type,
      product: o.product,
      time: o.order_timestamp || o.exchange_timestamp,
      filledQty: o.filled_quantity,
      tag: o.tag,
    }));
  };

  const allOrders = [
    ...parseOrders(mstockOrders, "mStock"),
    ...parseOrders(zerodhaOrders, "Zerodha"),
  ].sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));

  const totalRealisedPnl = allPositions.reduce((s, p) => s + (p.realised || 0), 0);
  const totalUnrealisedPnl = allPositions.reduce((s, p) => s + (p.unrealised || 0), 0);
  const totalPnl = totalRealisedPnl + totalUnrealisedPnl;

  return (
    <div className="five-ema">
      <div className="dashboard-header">
        <div>
          <h2>Positions & Orders</h2>
          <p className="dashboard-subtitle">Live positions and trading history across all brokers</p>
        </div>
        <div className="dashboard-actions">
          <button className="btn btn-primary" onClick={fetchAll} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {/* Auth Status */}
      <div className="ema-filters">
        <span className="filter-label">Connected:</span>
        <span className={`broker-status ${mstockAuth ? "broker-connected" : "broker-disconnected"}`}>
          mStock {mstockAuth ? "✓" : "✗"}
        </span>
        <span className={`broker-status ${zerodhaAuth ? "broker-connected" : "broker-disconnected"}`}>
          Zerodha {zerodhaAuth ? "✓" : "✗"}
        </span>
      </div>

      {/* P&L Summary */}
      <div className="scan-summary">
        <div className={`summary-card ${totalPnl >= 0 ? "" : "summary-card-red"}`}>
          <div className={`summary-value ${totalPnl >= 0 ? "positive" : "negative"}`} style={{fontSize:20}}>
            {totalPnl >= 0 ? "+" : ""}₹{Math.round(totalPnl).toLocaleString("en-IN")}
          </div>
          <div className="summary-label">Total P&L</div>
        </div>
        <div className="summary-card">
          <div className={`summary-value ${totalRealisedPnl >= 0 ? "positive" : "negative"}`}>
            {totalRealisedPnl >= 0 ? "+" : ""}₹{Math.round(totalRealisedPnl).toLocaleString("en-IN")}
          </div>
          <div className="summary-label">Realised</div>
        </div>
        <div className="summary-card">
          <div className={`summary-value ${totalUnrealisedPnl >= 0 ? "positive" : "negative"}`}>
            {totalUnrealisedPnl >= 0 ? "+" : ""}₹{Math.round(totalUnrealisedPnl).toLocaleString("en-IN")}
          </div>
          <div className="summary-label">Unrealised</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{openPositions.length}</div>
          <div className="summary-label">Open Positions</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{closedPositions.length}</div>
          <div className="summary-label">Closed Today</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{allOrders.length}</div>
          <div className="summary-label">Total Orders</div>
        </div>
      </div>

      {/* Tab selector */}
      <div className="bt-dir-filter" style={{marginBottom:16}}>
        <span className="bt-dir-label">View:</span>
        <button className={`bt-dir-btn ${activeView === "positions" ? "active" : ""}`} onClick={() => setActiveView("positions")}>Positions</button>
        <button className={`bt-dir-btn ${activeView === "orders" ? "active" : ""}`} onClick={() => setActiveView("orders")}>Orders</button>
      </div>

      {/* Open Positions */}
      {activeView === "positions" && (
        <>
          {openPositions.length > 0 && (
            <div className="trade-log-section">
              <h3>Open Positions</h3>
              <div className="stocks-table-wrapper">
                <table className="stocks-table">
                  <thead>
                    <tr>
                      <th>Broker</th>
                      <th>Symbol</th>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Avg Price</th>
                      <th>LTP</th>
                      <th>Unrealised P&L</th>
                      <th>Realised P&L</th>
                      <th>Total P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openPositions.map((p, i) => (
                      <tr key={i} className="trade-open-row">
                        <td><span className="broker-tag">{p.broker}</span></td>
                        <td className="symbol">{p.symbol}</td>
                        <td>{p.product}</td>
                        <td className={p.qty > 0 ? "positive" : "negative"}>{p.qty}</td>
                        <td>₹{p.avgPrice?.toFixed(2)}</td>
                        <td>₹{p.ltp?.toFixed(2)}</td>
                        <td className={p.unrealised >= 0 ? "positive" : "negative"}>
                          {p.unrealised >= 0 ? "+" : ""}₹{Math.round(p.unrealised || 0).toLocaleString("en-IN")}
                        </td>
                        <td className={p.realised >= 0 ? "positive" : "negative"}>
                          {p.realised >= 0 ? "+" : ""}₹{Math.round(p.realised || 0).toLocaleString("en-IN")}
                        </td>
                        <td className={p.pnl >= 0 ? "positive" : "negative"}>
                          {p.pnl >= 0 ? "+" : ""}₹{Math.round(p.pnl || 0).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Closed Positions */}
          {closedPositions.length > 0 && (
            <div className="trade-log-section">
              <h3>Closed Positions (Today)</h3>
              <div className="stocks-table-wrapper">
                <table className="stocks-table">
                  <thead>
                    <tr>
                      <th>Broker</th>
                      <th>Symbol</th>
                      <th>Product</th>
                      <th>Buy Qty</th>
                      <th>Sell Qty</th>
                      <th>Buy Value</th>
                      <th>Sell Value</th>
                      <th>P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedPositions.map((p, i) => (
                      <tr key={i}>
                        <td><span className="broker-tag">{p.broker}</span></td>
                        <td>{p.symbol}</td>
                        <td>{p.product}</td>
                        <td>{p.buyQty}</td>
                        <td>{p.sellQty}</td>
                        <td>₹{Math.round(p.buyValue || 0).toLocaleString("en-IN")}</td>
                        <td>₹{Math.round(p.sellValue || 0).toLocaleString("en-IN")}</td>
                        <td className={p.pnl >= 0 ? "positive" : "negative"}>
                          {p.pnl >= 0 ? "+" : ""}₹{Math.round(p.pnl || 0).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {allPositions.length === 0 && !loading && (
            <div className="scan-empty"><p>No positions found. Login to a broker and start trading.</p></div>
          )}
        </>
      )}

      {/* Orders */}
      {activeView === "orders" && (
        <div className="trade-log-section">
          <h3>Order History ({allOrders.length})</h3>
          {allOrders.length > 0 ? (
            <div className="stocks-table-wrapper">
              <table className="stocks-table">
                <thead>
                  <tr>
                    <th>Broker</th>
                    <th>Time</th>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Order Type</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Filled</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {allOrders.map((o, i) => (
                    <tr key={i} className={o.status === "COMPLETE" || o.status === "TRADED" ? "trade-win" : o.status === "REJECTED" || o.status === "CANCELLED" ? "trade-loss" : ""}>
                      <td><span className="broker-tag">{o.broker}</span></td>
                      <td>{formatDate(o.time)}</td>
                      <td>{o.symbol}</td>
                      <td>
                        <span className={`signal-badge ${o.type === "SELL" ? "badge-short" : "badge-long"}`}>
                          {o.type}
                        </span>
                      </td>
                      <td>{o.orderType}</td>
                      <td>{o.product}</td>
                      <td>{o.qty}</td>
                      <td>{o.filledQty || "-"}</td>
                      <td>{o.price ? `₹${o.price.toFixed(2)}` : "-"}</td>
                      <td>
                        <span className={`status-badge ${o.status === "COMPLETE" || o.status === "TRADED" ? "status-open" : o.status === "REJECTED" || o.status === "CANCELLED" ? "status-closed" : ""}`}>
                          {o.status}
                        </span>
                      </td>
                      <td className="opt-symbol-small">{o.orderId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="scan-empty"><p>No orders found today.</p></div>
          )}
        </div>
      )}
    </div>
  );
}
